import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Smartphone, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';

export const QrScan = ({ onResult }) => {
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const done = useRef(false);
  const started = useRef(false);

  const start = () => {
    setStatus('loading'); setError(''); done.current = false;
    axios.post(`${API}/scan-sessions`).then(r => { setSession(r.data); setStatus('waiting'); })
      .catch(() => { setStatus('error'); setError('Impossible de créer le QR code'); });
  };
  useEffect(() => { if (!started.current) { started.current = true; start(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/scan-sessions/${session.id}`);
        if (done.current) return;
        if (r.data.status === 'completed' && r.data.result) { done.current = true; clearInterval(t); setStatus('completed'); onResult(r.data.result); }
        else if (r.data.status === 'error') { setStatus('error'); setError(r.data.error || 'Lecture impossible'); }
        else setStatus(r.data.status);
      } catch { /* retry on next tick */ }
    }, 2000);
    return () => clearInterval(t);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex items-center gap-4" data-testid="qr-scan">
      <div className="w-40 h-40 shrink-0 rounded-xl border bg-white flex items-center justify-center overflow-hidden">
        {status === 'loading' && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
        {session && status !== 'loading' && <img src={session.qr_code} alt="QR code" className={`w-full h-full ${status === 'waiting' || status === 'error' ? '' : 'opacity-20'}`} data-testid="qr-scan-image" />}
      </div>
      <div className="text-sm space-y-2" data-testid="qr-scan-status">
        {status === 'waiting' && <>
          <p className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4" />Scannez avec votre téléphone</p>
          <ol className="text-slate-600 list-decimal ml-4 space-y-0.5"><li>Ouvrez l'appareil photo</li><li>Visez le QR code</li><li>Photographiez la carte grise</li></ol>
          <p className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />En attente de la photo…</p>
        </>}
        {status === 'processing' && <p className="font-semibold flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Photo reçue, lecture de la carte grise…</p>}
        {status === 'completed' && <p className="font-semibold text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Carte grise lue</p>}
        {status === 'error' && <>
          <p className="text-red-600 font-semibold">{error}</p>
          <p className="text-xs text-slate-500">Vous pouvez réessayer depuis le téléphone, ou :</p>
          <Button type="button" size="sm" variant="outline" onClick={start} data-testid="qr-scan-retry"><RefreshCw className="w-3.5 h-3.5 mr-1" />Nouveau QR code</Button>
        </>}
      </div>
    </div>
  );
};
